import { useEffect, useState } from 'react';
import { api } from '../lib/api.js';
import { useAuth } from '../lib/auth.jsx';
import { formatInTz } from '../lib/utcClock.js';
import { DEFAULT_TIMEZONE } from '../lib/timezones.js';
import { HeaderActions } from '../lib/pageHeader.jsx';
import Pagination, { PAGE_SIZE } from '../components/ui/Pagination.jsx';
import UserForm from './UserForm.jsx';

function Chip({ children, tone = 'bg-ink-100 text-ink-700' }) {
  return (
    <span className={`inline-flex items-center rounded px-1.5 py-0.5 text-[11px] font-medium ${tone}`}>
      {children}
    </span>
  );
}

export default function Users() {
  const { user: me } = useAuth();
  const tz = me?.timezone || DEFAULT_TIMEZONE;
  const [items, setItems] = useState(null);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [error, setError] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  async function load(opts = {}) {
    const off = opts.offset ?? offset;
    setError('');
    try {
      const r = await api.get(`/users?limit=${PAGE_SIZE}&offset=${off}`);
      setItems(r.items ?? []);
      setTotal(r.total ?? 0);
      setOffset(r.offset ?? 0);
    } catch (e) {
      setError(e.message);
    }
  }

  useEffect(() => { load({ offset: 0 }); }, []);

  function openNew() { setEditing(null); setFormOpen(true); }
  function openEdit(row) { setEditing(row); setFormOpen(true); }

  async function handleSaved() {
    setFormOpen(false);
    await load();
  }

  async function remove(row) {
    if (!confirm(`Delete user "${row.username}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${row.id}`);
      await load();
    } catch (e) {
      alert(e?.data?.error ?? e.message);
    }
  }

  return (
    <div className="space-y-4">
      <HeaderActions>
        <button className="btn-primary" onClick={openNew}>
          <i className="fa-solid fa-plus" /> New user
        </button>
      </HeaderActions>

      <div>
        <p className="text-sm text-ink-500">Manage admins and operators.</p>
      </div>

      {error && <div className="card p-4 text-sm text-red-700">{error}</div>}

      <div className="card overflow-hidden">
        {items === null ? (
          <div className="p-6 text-sm text-ink-500">Loading…</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-sm text-ink-500">No users.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-ink-100 bg-ink-50 text-left text-xs font-semibold uppercase tracking-wider text-ink-500">
                  <th className="px-4 py-2.5">Username</th>
                  <th className="px-4 py-2.5">Full name</th>
                  <th className="px-4 py-2.5">Role</th>
                  <th className="px-4 py-2.5">Created</th>
                  <th className="px-4 py-2.5">Updated</th>
                  <th className="px-4 py-2.5 text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {items.map((row) => {
                  const isSelf = me && row.id === me.id;
                  return (
                    <tr key={row.id} className="border-b border-ink-100 last:border-b-0">
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <span className="font-semibold text-ink-900">{row.username}</span>
                          {isSelf && <Chip tone="bg-brand-50 text-brand-700">you</Chip>}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-ink-700">{row.fullname || <em className="text-ink-400">—</em>}</td>
                      <td className="px-4 py-3">
                        {row.role === 'admin'
                          ? <Chip tone="bg-brand-100 text-brand-700">admin</Chip>
                          : <Chip tone="bg-ink-100 text-ink-700">user</Chip>}
                      </td>
                      <td className="px-4 py-3 font-mono text-xs text-ink-500">{formatInTz(row.created_at, tz)}</td>
                      <td className="px-4 py-3 font-mono text-xs text-ink-500">{formatInTz(row.updated_at, tz)}</td>
                      <td className="px-4 py-3 text-right">
                        <div className="inline-flex items-center gap-1">
                          <button className="btn-ghost p-2" onClick={() => openEdit(row)} title="Edit">
                            <i className="fa-solid fa-pen-to-square" />
                          </button>
                          <button
                            className="btn-ghost p-2 text-red-600 hover:bg-red-50 disabled:opacity-40 disabled:hover:bg-transparent"
                            onClick={() => remove(row)}
                            disabled={isSelf}
                            title={isSelf ? 'Cannot delete yourself' : 'Delete'}
                          >
                            <i className="fa-solid fa-trash" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {items && items.length > 0 && (
          <Pagination
            total={total}
            offset={offset}
            limit={PAGE_SIZE}
            onChange={(o) => load({ offset: o })}
          />
        )}
      </div>

      {formOpen && (
        <UserForm
          editing={editing}
          currentUserId={me?.id}
          onClose={() => setFormOpen(false)}
          onSaved={handleSaved}
        />
      )}
    </div>
  );
}
